import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import issueService from '../../services/issueService';

const IssueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIssue();
  }, [id]);

  const loadIssue = async () => {
    try {
      setLoading(true);
      const data = await issueService.getById(id);
      setIssue(data);
    } catch (error) {
      console.error('Error loading issue:', error);
      alert('Failed to load issue');
      navigate('/app/issues');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this issue?')) {
      try {
        await issueService.delete(id);
        navigate('/app/issues');
      } catch (error) {
        console.error('Error deleting issue:', error);
        alert('Failed to delete issue');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading issue...</div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Issue not found</p>
        <Link to="/app/issues" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
          Back to Issues
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <Link
              to="/app/issues"
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back to Issues
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{issue.title}</h1>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/app/issues/${issue.id}/edit`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Issue Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
            {issue.description ? (
              <p className="text-gray-700 whitespace-pre-wrap">{issue.description}</p>
            ) : (
              <p className="text-gray-400 italic">No description provided</p>
            )}
          </div>

          {/* Resolution */}
          {(issue.status === 'Resolved' || issue.status === 'Closed') && issue.resolution && (
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <h2 className="text-lg font-semibold text-green-900 mb-4">Resolution</h2>
              <p className="text-green-800 whitespace-pre-wrap">{issue.resolution}</p>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-500 rounded-full"></div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Issue Reported</p>
                  <p className="text-sm text-gray-500">
                    {new Date(issue.reported_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    by {issue.reporter?.first_name} {issue.reporter?.last_name}
                  </p>
                </div>
              </div>

              {issue.resolved_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-green-500 rounded-full"></div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">Issue Resolved</p>
                    <p className="text-sm text-gray-500">
                      {new Date(issue.resolved_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-4">Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${issueService.getStatusColor(issue.status)}`}>
                  {issue.status}
                </span>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Severity</p>
                <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${issueService.getSeverityColor(issue.severity)}`}>
                  {issue.severity}
                </span>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Type</p>
                <div className="flex items-center">
                  <span className="text-xl mr-2">{issueService.getIssueTypeIcon(issue.issue_type)}</span>
                  <span className="text-sm font-medium text-gray-900">{issue.issue_type}</span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Project</p>
                <Link
                  to={`/app/projects/${issue.project?.id}`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  {issue.project?.name}
                </Link>
              </div>
            </div>
          </div>

          {/* People Card */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-4">People</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">Reported By</p>
                {issue.reporter && (
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                      {issue.reporter.first_name[0]}{issue.reporter.last_name[0]}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {issue.reporter.first_name} {issue.reporter.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{issue.reporter.email}</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Assigned To</p>
                {issue.assignee ? (
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white font-medium">
                      {issue.assignee.first_name[0]}{issue.assignee.last_name[0]}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {issue.assignee.first_name} {issue.assignee.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{issue.assignee.email}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Unassigned</p>
                )}
              </div>
            </div>
          </div>

          {/* Metadata Card */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-4">Metadata</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500">Created</p>
                <p className="text-gray-900 font-medium">
                  {new Date(issue.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Last Updated</p>
                <p className="text-gray-900 font-medium">
                  {new Date(issue.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueDetail;


